import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Download, TrendUp, Package, Users, CalendarBlank } from "@phosphor-icons/react";
import { API } from "@/lib/api";

export default function Dashboard() {
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/dashboard/revenue-series", { params: { days: 30 } }).then((r) => setSeries(r.data));
    api.get("/dashboard/top-products").then((r) => setTop(r.data));
  }, []);

  const exportCsv = () => { window.open(`${API}/dashboard/export`, "_blank"); };

  if (!summary) return <p>Loading…</p>;

  const stats = [
    { label: t("admin.revenue_today"), value: formatIDR(summary.today_revenue), sub: `${summary.today_orders} orders`, icon: TrendUp },
    { label: t("admin.revenue_week"), value: formatIDR(summary.week_revenue), sub: `${summary.week_orders} orders`, icon: TrendUp },
    { label: t("admin.revenue_month"), value: formatIDR(summary.month_revenue), sub: `${summary.month_orders} orders • ${summary.month_events} events`, icon: TrendUp },
    { label: t("admin.new_inquiries"), value: summary.new_inquiries, sub: "reservations", icon: CalendarBlank },
    { label: t("admin.pending_orders"), value: summary.pending_orders, sub: "to process", icon: Package },
    { label: t("admin.customers"), value: summary.customers_total, sub: "total", icon: Users },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{t("admin.dashboard")}</h1>
          <p className="text-sm text-zinc-500 mt-1">Business overview & revenue</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="dash-export"><Download size={14} className="mr-2" />{t("admin.export")}</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dash-stats">
        {stats.map((s) => (
          <Card key={s.label} className="border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</p>
                <s.icon size={18} className="text-zinc-400" />
              </div>
              <p className="mt-3 text-3xl font-mono-stat font-bold" style={{ fontFamily: "JetBrains Mono" }}>{s.value}</p>
              <p className="mt-1 text-xs text-zinc-500">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-zinc-200">
          <CardHeader><CardTitle className="text-sm">Revenue — last 30 days</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v / 1000}k`} />
                  <Tooltip formatter={(v) => formatIDR(v)} />
                  <Line dataKey="beans" stroke="#8C4A32" strokeWidth={2} dot={false} name="Beans" />
                  <Line dataKey="events" stroke="#4A5D4E" strokeWidth={2} dot={false} name="Events" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardHeader><CardTitle className="text-sm">Top Products</CardTitle></CardHeader>
          <CardContent>
            {top.length === 0 ? (
              <p className="text-xs text-zinc-500">No sales yet.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top} margin={{ left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip formatter={(v) => formatIDR(v)} />
                    <Bar dataKey="revenue" fill="#8C4A32" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
